import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  ChevronDown,
  MapPin,
  Ticket,
} from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

const HeroSection: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?q=80&w=2070')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-romanian-blue/80 via-romanian-blue/60 to-romanian-blue/90" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-romanian-yellow/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-romanian-red/20 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 container-custom text-center px-4 pt-24">
        {/* Romanian Ornament */}
        <div className="flex items-center justify-center space-x-4 mb-8">
          <div className="w-16 h-1 bg-gradient-to-r from-transparent to-romanian-yellow" />
          <div className="w-3 h-3 bg-romanian-yellow rotate-45" />
          <div className="w-16 h-1 bg-gradient-to-l from-transparent to-romanian-yellow" />
        </div>

        {/* Main Title */}
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 animate-fade-in">
          Ca Pe Vremuri
        </h1>

        {/* Subtitle */}
        <p className="text-romanian-yellow text-xl md:text-2xl font-display mb-4 animate-fade-in">
          {t.hero.subtitle}
        </p>

        {/* Description */}
        <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto mb-8 leading-relaxed animate-slide-up">
          {t.hero.description}
        </p>

        {/* Event Info */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-10 animate-slide-up">
          <div className="flex items-center space-x-2 text-white/90">
            <Calendar className="w-5 h-5 text-romanian-yellow" />
            <span>06 APRILE 2026 • 15:00</span>
          </div>
          <div className="hidden sm:block w-1 h-1 bg-romanian-yellow rounded-full" />
          <div className="flex items-center space-x-2 text-white/90">
            <MapPin className="w-5 h-5 text-romanian-yellow" />
            <span>Pala Ruffini, Torino</span>
          </div>
        </div>

        {/* Ticket Pricing */}
        <div className="animate-slide-up mb-6 sm:mb-10">
          <div className="flex items-center justify-center gap-2 mb-3 sm:mb-4">
            <Ticket className="w-4 h-4 sm:w-5 sm:h-5 text-romanian-yellow" />
            <span className="text-romanian-yellow font-display text-base sm:text-lg font-semibold tracking-wide uppercase">
              {t.hero.pricingTitle}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
            {/* VIP / Participant */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-xl px-3 py-2 sm:px-5 sm:py-3 text-center min-w-[140px] sm:min-w-[180px]">
              <p className="text-white/70 text-[10px] sm:text-xs uppercase tracking-wider mb-0.5 sm:mb-1">
                {t.hero.participant}
              </p>
              <p className="text-white text-lg sm:text-2xl font-bold">€35</p>
              <p className="text-white/50 text-[9px] sm:text-xs mt-0.5">
                +€3,99 {t.hero.commission}
              </p>
            </div>
            {/* Normal / Spectator */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-xl px-3 py-2 sm:px-5 sm:py-3 text-center min-w-[140px] sm:min-w-[180px]">
              <p className="text-white/70 text-[10px] sm:text-xs uppercase tracking-wider mb-0.5 sm:mb-1">
                {t.hero.spectator}
              </p>
              <p className="text-white text-lg sm:text-2xl font-bold">€25</p>
              <p className="text-white/50 text-[9px] sm:text-xs mt-0.5">
                +€3,99 {t.hero.commission}
              </p>
            </div>
            {/* Child */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-xl px-3 py-2 sm:px-5 sm:py-3 text-center min-w-[140px] sm:min-w-[180px]">
              <p className="text-white/70 text-[10px] sm:text-xs uppercase tracking-wider mb-0.5 sm:mb-1">
                {t.hero.child}
              </p>
              <p className="text-romanian-yellow text-lg sm:text-2xl font-bold">
                {t.hero.free}
              </p>
              <p className="text-white/50 text-[9px] sm:text-xs mt-0.5">
                &nbsp;
              </p>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up">
          <Link
            to="/acces"
            className="btn-secondary group flex items-center space-x-2 px-8 py-4 text-lg"
          >
            <span>{t.hero.cta}</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/despre-noi"
            className="hidden sm:flex btn-outline text-white border-white hover:bg-white hover:text-romanian-blue px-8 py-4 text-lg items-center"
          >
            {t.hero.learnMore}
          </Link>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 120L48 110C96 100 192 80 288 70C384 60 480 60 576 65C672 70 768 80 864 85C960 90 1056 90 1152 85C1248 80 1344 70 1392 65L1440 60V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0Z"
            fill="#fafafa"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
